import { useTranslation } from 'react-i18next'
import { Controller, useFormContext } from 'react-hook-form'
import { H3, H4, RadioInput, Span } from '@/components/atoms'
import { DatePickerField, SelectField } from '@/components/molecules'
import { usePetFormStepTwo } from './page.onboarding.pet-form-step-two.hook'
import type { PetFormValues } from '../page.onboarding.pet-form.hook'
import type { Pet } from '@/shared/types/global.types'

interface PetFormStepTwoPageProps {
  pet?: Pet
  showVetPrimary?: boolean
}

export function PetFormStepTwoPage({
  pet,
  showVetPrimary,
}: PetFormStepTwoPageProps) {
  const { t } = useTranslation()
  const { control } = useFormContext<PetFormValues>()
  const {
    vetOptions,
    setVetSearch,
    handleSelectVet,
    yesNoOptions,
    isVetSearchLoading,
  } = usePetFormStepTwo({ pet })

  return (
    <div className="flex flex-col gap-8">
      <div className="flex flex-col gap-4 md:hidden">
        <H3>
          {`${t('onboarding.pet.form.titleStart')} `}
          <span className="text-indigo-base capitalize">{`${pet?.name}!`}</span>
        </H3>
        <Span className="text-sm leading-6 text-neutral-900">
          {t('onboarding.pet.form.subtitle')}
        </Span>
        <H4>{t('onboarding.pet.form.stepTwo.stepLabel')}</H4>
      </div>
      <H3 className="hidden md:block">
        {t('onboarding.pet.form.stepTwo.stepLabel')}
      </H3>

      <div className="flex w-full flex-col gap-6 md:w-72.5">
        {!showVetPrimary && (
          <Controller
            control={control}
            name="primaryVetId"
            render={({ field }) => (
              <SelectField
                label={t('onboarding.pet.form.stepTwo.vetLabel')}
                placeholder={t('onboarding.pet.form.stepTwo.vetPlaceholder')}
                optionsPlaceholder={t(
                  'onboarding.pet.form.stepTwo.vetOptionsPlaceholder',
                )}
                optional
                searchable
                loading={isVetSearchLoading}
                options={vetOptions}
                value={field.value}
                onSearch={setVetSearch}
                onSelect={handleSelectVet}
                onBlur={field.onBlur}
              />
            )}
          />
        )}
        <Controller
          control={control}
          name="lastVaccinationDate"
          render={({ field }) => (
            <DatePickerField
              label={t('onboarding.pet.form.stepTwo.vaccinationLabel')}
              value={field.value}
              placeholder="00/0000"
              optional
              onChange={field.onChange}
              onBlur={field.onBlur}
              maxDate={new Date()}
              dropdownMonthYear
              panelPosition="relative"
              dateFormatOptions={{ month: '2-digit', year: 'numeric' }}
            />
          )}
        />
      </div>

      <div className="flex flex-col gap-3">
        <Span className="tracking-base text-base leading-6 text-neutral-900">
          {t('onboarding.pet.form.stepTwo.microchipQuestion')}
        </Span>
        <Controller
          control={control}
          name="hasMicrochip"
          render={({ field }) => (
            <div className="flex flex-wrap gap-4 pl-1.5">
              {yesNoOptions.map((option) => (
                <RadioInput
                  key={option.value}
                  name="hasMicrochip"
                  label={option.label}
                  value={option.value}
                  checked={field.value === option.value}
                  onChange={() => field.onChange(option.value)}
                />
              ))}
            </div>
          )}
        />
      </div>

      <div className="flex flex-col gap-3">
        <Span className="tracking-base text-base leading-6 text-neutral-900">
          {t('onboarding.pet.form.stepTwo.neuteredQuestion')}
        </Span>
        <Controller
          control={control}
          name="isNeutered"
          render={({ field }) => (
            <div className="flex flex-wrap gap-4 pl-1.5">
              {yesNoOptions.map((option) => (
                <RadioInput
                  key={option.value}
                  name="isNeutered"
                  label={option.label}
                  value={option.value}
                  checked={field.value === option.value}
                  onChange={() => field.onChange(option.value)}
                />
              ))}
            </div>
          )}
        />
      </div>
    </div>
  )
}
